import XXHFactory from 'xxhash-wasm';

let xxhInitPromise: ReturnType<typeof XXHFactory> | null = null;

async function getXXH() {
  if (!xxhInitPromise) {
    xxhInitPromise = XXHFactory();
  }
  return xxhInitPromise;
}

const FINGERPRINT_SLICE_SIZE = 8 * 1024; // 8KB

/**
 * Generates a content fingerprint for a file using first 8KB, last 8KB and file size.
 * Uses xxHash32 via xxhash-wasm for speed and shorter output.
 * Format: `${fileSize}-${startHash32}-${endHash32}` where each hash is 8 hex chars.
 */
export async function createFileFingerprintWithWorker(file: File): Promise<string> {
  const fileSize = file.size;
  const startSlice = file.slice(0, Math.min(FINGERPRINT_SLICE_SIZE, fileSize));
  const endSliceStart = Math.max(0, fileSize - FINGERPRINT_SLICE_SIZE);
  const endSlice = file.slice(endSliceStart, fileSize);

  const [startBuf, endBuf] = await Promise.all([startSlice.arrayBuffer(), endSlice.arrayBuffer()]);

  const { h32Raw } = await getXXH();
  const toHex8 = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  const startHash = toHex8(h32Raw(new Uint8Array(startBuf)));
  const endHash = toHex8(h32Raw(new Uint8Array(endBuf)));

  const fingerprint = `${fileSize}-${startHash}-${endHash}`;
  dedupDebugLog('fingerprint', { name: file.name, size: fileSize, fingerprint });
  return fingerprint;
}

export type DuplicateCheckResult = {
  isDuplicate: boolean;
  matched?: { _id: string };
  fingerprint?: string;
};

// Minimal shape needed to perform duplicate checks
export type DuplicateCandidate = {
  _id: string;
  uniqueId?: string;
  metadata?: { filename?: string };
};

/**
 * Two-stage duplicate detection:
 *  1) Fast filename comparison
 *  2) If same-name candidates exist, compute fingerprint and compare to existing metadata.fingerprint
 */
export async function checkForDuplicate(
  file: File,
  existingFiles: DuplicateCandidate[],
): Promise<DuplicateCheckResult> {
  const fingerprint = await createFileFingerprintWithWorker(file);

  // Stage 0: uniqueId match across all existing items
  const byUniqueId = (existingFiles || []).find((m) => m.uniqueId === fingerprint);
  if (byUniqueId) {
    dedupDebugLog('stage0: uniqueId duplicate', {
      name: file.name,
      fingerprint,
      matched: byUniqueId._id,
    });
    return { isDuplicate: true, matched: { _id: byUniqueId._id }, fingerprint };
  }

  // Stage 1: Fast filename comparison
  const filenameMatches = (existingFiles || []).filter(
    (m) => m.metadata?.filename && m.metadata.filename === file.name,
  );
  dedupDebugLog('stage1: filename matches', { name: file.name, matches: filenameMatches.length });

  if (filenameMatches.length === 0) {
    return { isDuplicate: false, fingerprint };
  }

  // Stage 2: No additional content comparison needed.
  // uniqueId IS the content fingerprint; if Stage 0 didn't match, treat as non-duplicate here.
  dedupDebugLog('stage2: uniqueId is fingerprint; no extra checks', {
    name: file.name,
    fingerprint,
  });
  return { isDuplicate: false, fingerprint };
}

export function dedupDebugLog(...args: any[]) {
  try {
    if (
      typeof window !== 'undefined' &&
      window?.localStorage?.getItem('DEBUG_UPLOAD_DEDUP') === '1'
    ) {
      // eslint-disable-next-line no-console
      console.log('[dedup]', ...args);
    }
  } catch (_) {
    // ignore
  }
}
